import Bluebird from 'bluebird';
import xml2js from 'xml2js';
import each from 'lodash/each';
import isPlainObject from 'lodash/isPlainObject';

const ARRAY_TAGS = [
  'error',
  'ad',
  'impression',
  'category',
  'survey',
  'viewable',
  'notViewable',
  'viewUndetermined',
  'creative',
  'creativeExtension',
  'mediaFile',
  'InteractiveCreativeFile',
  'clickTracking',
  'customClick',
  'icon',
  'iconViewTracking',
  'iconClickTracking',
  'nonLinear',
  'nonLinearClickTracking',
  'companion',
  'companionClickTracking',
  'tracking',
  'staticResource',
  'iFrameResource',
  'htmlResource',
  'verification',
  'javaScriptResource',
  'flashResource',
  'extension',
];

const TAG_ARRAY_OBJ = ARRAY_TAGS.reduce((accum, tag) => {
  const result = accum;
  result[tag] = true;
  return result;
}, {});

function tagProcessor(n) {
  let name = n;
  if (name.indexOf('HTML') > -1) {
    name = name.replace('HTML', 'html');
  } else if (name.indexOf('VAST') > -1) {
    name = name.replace('VAST', 'vast');
  }
  return name;
}

const Parser = new xml2js.Parser({
  tagNameProcessors: [tagProcessor, xml2js.processors.firstCharLowerCase],
  attrNameProcessors: [xml2js.processors.firstCharLowerCase],
  explicitArray: false,
  explicitCharkey: true,
  explicitAttrkey: true,
  charkey: '_value',
  attrkey: '_attr',
});

function getValue() {
  try {
    return this._value;
  } catch (error) {
    return null;
  }
}

function getAttr(attribute) {
  try {
    return this._attr[attribute];
  } catch (error) {
    return null;
  }
}

function objectTransform(object) {
  const result = Array.isArray(object) ? [] : {};
  each(object, (v, key) => {
    const value = v;
    const isPlainObject = isPlainObject(value);
    const isArray = Array.isArray(value);
    if (isPlainObject) {
      if (value._value) {
        value.getValue = getValue.bind(value);
      }
      value.getAttr = getAttr.bind(value);
    }
    if (isPlainObject || isArray) {
      result[key] = TAG_ARRAY_OBJ[key] && !isArray ?
        [objectTransform(value)] : objectTransform(value);
    } else {
      result[key] = value;
    }
  });
  return result;
}

const parseString = Bluebird.promisify(Parser.parseString);

async function parseVAST(xml) {
  return await new Promise((resolve, reject) => {
    parseString(xml)
      .then(json => resolve(objectTransform(json)))
      .catch(error => reject(error));
  });
}

function isValidUrl(string) {
  try {
    new URL(string);
    return true;
  } catch (error) {
    return false;
  }
}

function adObject(vast, baseURL) {
  try {
    const { vast: { ad } } = vast,
      result = [];
    ad.map(item => {
      const { inLine, inLine: { creatives: { creative } } } = item;
      creative.map(adCreative => {
        const { linear, linear: { videoClicks: { clickThrough }, mediaFiles: { mediaFile } } } = adCreative;
        result.push({
          adTitle: inLine.adTitle.getValue(),
          adSystem: inLine.adSystem.getValue(),
          duration: hmsToSecondsOnly(linear.duration.getValue()),
          skipOn: hmsToSecondsOnly(linear.getAttr('skipoffset')),
          adID: adCreative.getAttr('adID'),
          videoClicks: clickThrough.getValue(),
          mediaFiles: mediaFile.map(media => {
            let adURL = media.getValue();
            if (!isValidUrl(adURL) && baseURL) {
              adURL = `${baseURL}${adURL.startsWith('/') ? '' : '/'}${adURL}`;
            }

            return {
              URL: adURL,
              type: media.getAttr('type'),
              width: media.getAttr('width'),
              height: media.getAttr('height'),
              delivery: media.getAttr('delivery'),
            };
          })
        });
      });
    });
    return result;
  } catch (error) {
    return [];
  }
}

async function fetchVast(URL) {
  return await new Promise(async (resolve, reject) => {
    try {
      const response = await timeoutFetch(URL, { method: 'GET' }, 5000);
      if (response.status !== 200) {
        return reject('INVALID_RESPONSE_STATUS_CODE');
      }
      resolve(await response.text());
    } catch (error) {
      reject(error);
    }
  });
}

function hmsToSecondsOnly(stringTime) {
  if (!stringTime || stringTime.split(':').length !== 3) {
    return 0;
  }

  let p = stringTime && stringTime.split(':'),
    s = 0,
    m = 1;

  while (p.length > 0) {
    s += m * parseInt(p.pop(), 10);
    m *= 60;
  }

  return s;
}

export async function handleVAST(sourceURL) {
  return await new Promise(async (resolve, reject) => {
    try {
      const XMLData = await fetchVast(sourceURL),
        domainURL = new URL(sourceURL);
      resolve(
        adObject(
          await parseVAST(XMLData),
          domainURL && domainURL.origin
        )
      );
    } catch (error) {
      reject(error);
    }
  });
}

export function timeoutFetch(url, options, timeout) {
  return Promise.race([
    fetch(url, options),
    new Promise((_, reject) =>
      setTimeout(() => reject('FILE_FETCH_TIMEOUT'), timeout)
    )
  ]);
}
